import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useMapsLibrary } from '@vis.gl/react-google-maps';
import { TextField, InputAdornment, Alert, Box } from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PlaceAutocompleteElementWrapper from './PlaceAutocompleteElementWrapper';
import { getServiceAreaBias } from '../../utils/serviceAreaBias';
import { NO_AUTOFILL } from '../../utils/noAutofill';
import { ensureRoomBelow } from '../../utils/ensureRoomBelow';
import {
  noteSuggestionInput,
  noteSuggestionSelected,
  noteSuggestionKey,
  noteSuggestionClosed,
} from '../../utils/placeSuggestionState';
import { parseAddressComponents } from '../../utils/addressComponents';

// Verwende neue Places API als Standard
const USE_NEW_PLACES_API = true;

const PlaceAutocomplete = ({ 
  onPlaceSelect, 
  label, 
  value = '', 
  onChange,
  error,
  helperText,
  required = false,
  disabled = false,
  fullWidth = true,
  // Name für die Tastaturführung (useFormKeyboardNav)
  navName = null,
  ...textFieldProps
}) => {
  const [placeAutocomplete, setPlaceAutocomplete] = useState(null);
  const [useNewApi, setUseNewApi] = useState(USE_NEW_PLACES_API);
  const [apiError, setApiError] = useState(null);
  const [isEditingMode, setIsEditingMode] = useState(false);
  // Nur als vorausgefüllt betrachten, wenn beim Mount bereits ein Wert vorhanden war
  const [mountedWithValue] = useState(() => value && value.trim().length > 0);
  const [hasPrefilledValue, setHasPrefilledValue] = useState(() => value && value.trim().length > 0);
  const inputRef = useRef(null);
  const places = useMapsLibrary('places');

  // Betriebsgebiet als lokalen Bias für die Adresssuche ableiten
  // (siehe docs/betriebsgebiet-vorbestellungs-disposition-konzept.md).
  const globalSettings = useSelector((state) => state.settings.global);
  const serviceAreaBias = useMemo(
    () => getServiceAreaBias(globalSettings),
    [globalSettings]
  );

  // Alle useEffect-Hooks MÜSSEN vor jedem konditionalen Return stehen

  // Prüfe ob ein Wert vorhanden ist (für Bearbeitungsmodus)
  // Nur relevant wenn die Komponente mit einem Wert gemountet wurde
  useEffect(() => {
    if (mountedWithValue && value && value.trim().length > 0 && !isEditingMode) {
      setHasPrefilledValue(true);
    } else {
      setHasPrefilledValue(false);
    }
  }, [value, isEditingMode, mountedWithValue]);
  
  // Automatische Erkennung der verfügbaren API
  useEffect(() => {
    if (!places) return;
    
    // Prüfe ob neue API verfügbar ist
    const hasNewApi = places.PlaceAutocompleteElement && typeof places.PlaceAutocompleteElement === 'function';
    const hasOldApi = places.Autocomplete && typeof places.Autocomplete === 'function';
    
    if (hasNewApi) {
      setUseNewApi(true);
    } else if (hasOldApi) {
      setUseNewApi(false);
    } else {
      setApiError('Keine Places API verfügbar');
    }
  }, [places]);

  // useEffect für alte API - muss vor return stehen, aber nur wenn alte API verwendet wird
  useEffect(() => {
    if (!places || !inputRef.current || useNewApi || apiError || hasPrefilledValue) return;

    const options = {
      componentRestrictions: { country: 'de' },
      fields: ['formatted_address', 'geometry', 'name'],
    };

    // Lokale Treffer aus dem Betriebsgebiet bevorzugen (weicher Bias, kein
    // harter Ausschluss -> externe Adressen bleiben auffindbar).
    if (serviceAreaBias?.bounds) {
      options.bounds = serviceAreaBias.bounds;
      options.strictBounds = false;
    }

    const autocomplete = new places.Autocomplete(inputRef.current, options);
    setPlaceAutocomplete(autocomplete);

    return () => {
      if (autocomplete) {
        window.google.maps.event.clearInstanceListeners(autocomplete);
      }
    };
  }, [places, useNewApi, apiError, hasPrefilledValue, serviceAreaBias]);

  useEffect(() => {
    if (!placeAutocomplete || useNewApi || apiError || hasPrefilledValue) return;

    placeAutocomplete.addListener('place_changed', () => {
      const place = placeAutocomplete.getPlace();
      
      if (!place.geometry || !place.geometry.location) {
        console.error('Keine Geometriedaten für diesen Ort gefunden');
        return;
      }

      const address = place.formatted_address || '';
      const name = place.name || '';
      // Wenn ein Ortsname vorhanden ist und nicht bereits in der Adresse enthalten, voranstellen
      const formattedAddress = name && address && !address.startsWith(name)
        ? `${name}, ${address}`
        : address || name;
      const location = {
        latitude: place.geometry.location.lat(),
        longitude: place.geometry.location.lng()
      };

      // Ort bestätigt: Ab jetzt darf Enter weiterspringen, statt erneut an die
      // Vorschlagsliste zu gehen (Konzept 3.4).
      if (inputRef.current) {
        noteSuggestionSelected(inputRef.current.dataset, formattedAddress);
      }

      onPlaceSelect(formattedAddress, location, parseAddressComponents(place.address_components));
    });
  }, [placeAutocomplete, onPlaceSelect, useNewApi, apiError, hasPrefilledValue]);

  // Zusätzlicher Effect für sofortige Dropdown-Positionierung - nur für alte API
  useEffect(() => {
    if (!inputRef.current || useNewApi || apiError || hasPrefilledValue) return;

    const handleInput = () => {
      // Verwende requestAnimationFrame für besseres Timing
      requestAnimationFrame(() => {
        // Finde den spezifischen pac-container für dieses Eingabefeld
        const pacContainer = findAssociatedPacContainer();
        if (pacContainer && inputRef.current) {
          const textFieldElement = inputRef.current.closest('.MuiTextField-root');
          const targetElement = textFieldElement || inputRef.current;
          const targetRect = targetElement.getBoundingClientRect();
          
          pacContainer.style.width = `${targetRect.width}px`;
          pacContainer.style.left = `${targetRect.left}px`;
          pacContainer.style.top = `${targetRect.bottom}px`;
          pacContainer.style.position = 'fixed';
          pacContainer.style.zIndex = '1500';
        }
      });
    };

    // Füge Listener für Input-Events hinzu
    inputRef.current.addEventListener('input', handleInput);
    inputRef.current.addEventListener('focus', handleInput);

    return () => {
      if (inputRef.current) {
        inputRef.current.removeEventListener('input', handleInput);
        inputRef.current.removeEventListener('focus', handleInput);
      }
    };
  }, [useNewApi, apiError, hasPrefilledValue]);

  // Verbesserte Scroll-Behandlung und Dropdown-Positionierung - nur für alte API
  useEffect(() => {
    if (!inputRef.current || !placeAutocomplete || useNewApi || apiError || hasPrefilledValue) return;

    // Funktion zum Schließen des Dropdowns beim Scrollen
    const handleScroll = () => {
      if (inputRef.current && document.activeElement === inputRef.current) {
        inputRef.current.blur();
      }
    };

    // Funktion zur Anpassung der Dropdown-Breite
    const adjustDropdownWidth = () => {
      // Finde den spezifischen pac-container für dieses Eingabefeld
      const pacContainer = findAssociatedPacContainer();
      if (pacContainer && inputRef.current) {
        // Finde das übergeordnete TextField-Element für korrekte Positionierung
        const textFieldElement = inputRef.current.closest('.MuiTextField-root');
        const targetElement = textFieldElement || inputRef.current;
        const targetRect = targetElement.getBoundingClientRect();
        
        pacContainer.style.width = `${targetRect.width}px`;
        pacContainer.style.left = `${targetRect.left}px`;
        pacContainer.style.top = `${targetRect.bottom}px`;
      }
    };

    // Observer für DOM-Änderungen (wenn das Dropdown erscheint)
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList') {
          // Prüfe ob ein pac-container hinzugefügt wurde
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === 1 && node.classList && node.classList.contains('pac-container')) {
              // Sofort anpassen, ohne Verzögerung
              adjustDropdownWidth();
              
              // Event Listener für Fenster-Resize
              const handleResize = () => adjustDropdownWidth();
              window.addEventListener('resize', handleResize);
              
              // Cleanup wenn das Dropdown verschwindet
              const cleanupObserver = new MutationObserver(() => {
                if (!findAssociatedPacContainer()) {
                  window.removeEventListener('resize', handleResize);
                  cleanupObserver.disconnect();
                }
              });
              cleanupObserver.observe(document.body, { childList: true, subtree: true });
            }
          });
        }
      });
    });

    // Überwache Änderungen am Body (wo das Dropdown eingefügt wird)
    observer.observe(document.body, { childList: true, subtree: true });

    // Scroll-Event-Listener hinzufügen
    let scrollableParent = inputRef.current.parentElement;
    const scrollListeners = [];

    while (scrollableParent) {
      if (scrollableParent.scrollHeight > scrollableParent.clientHeight ||
          scrollableParent === document.body ||
          scrollableParent === document.documentElement) {
        scrollableParent.addEventListener('scroll', handleScroll, true);
        scrollListeners.push({ element: scrollableParent, handler: handleScroll });
      }
      scrollableParent = scrollableParent.parentElement;
    }

    window.addEventListener('scroll', handleScroll, true);
    scrollListeners.push({ element: window, handler: handleScroll });

    return () => {
      observer.disconnect();
      scrollListeners.forEach(({ element, handler }) => {
        element.removeEventListener('scroll', handler, true);
      });
    };
  }, [placeAutocomplete, useNewApi, apiError, hasPrefilledValue]);

  // Legacy-Pfad: Solange der pac-Container sichtbar ist, gehört Enter der
  // Vorschlagsliste. Ohne diese Markierung würde die Tastaturführung
  // weiterspringen und eine Adresse ohne Koordinaten übernehmen (Konzept 3.4).
  useEffect(() => {
    if (!inputRef.current || useNewApi || apiError || hasPrefilledValue) return;
    const input = inputRef.current;

    const handleTyping = () => noteSuggestionInput(input.dataset, input.value || '');
    const handleKeys = (event) => noteSuggestionKey(input.dataset, event.key);
    const handleBlur = () => noteSuggestionClosed(input.dataset);

    input.addEventListener('input', handleTyping);
    input.addEventListener('keydown', handleKeys);
    input.addEventListener('blur', handleBlur);
    return () => {
      input.removeEventListener('input', handleTyping);
      input.removeEventListener('keydown', handleKeys);
      input.removeEventListener('blur', handleBlur);
    };
  }, [useNewApi, apiError, hasPrefilledValue]);

  // Helper-Funktion um den spezifischen pac-container für dieses Eingabefeld zu finden
  const findAssociatedPacContainer = () => {
    if (!inputRef.current) return null;
    
    // Alle pac-container finden
    const allPacContainers = document.querySelectorAll('.pac-container');
    
    // Wenn nur einer vorhanden ist, verwende diesen
    if (allPacContainers.length === 1) {
      return allPacContainers[0];
    }
    
    // Verbesserter Ansatz: Finde den Container der aktuell fokussiert ist
    // durch Überprüfung der Position und Sichtbarkeit
    const inputRect = inputRef.current.getBoundingClientRect();
    
    for (let container of allPacContainers) {
      const style = window.getComputedStyle(container);
      
      // Überprüfe ob Container sichtbar und positioniert ist
      if (style.display !== 'none' && style.visibility !== 'hidden') {
        const containerRect = container.getBoundingClientRect();
        
        // Prüfe ob der Container in der Nähe des Input-Feldes positioniert ist
        // (innerhalb eines Bereichs von 50px)
        const isNearInput = Math.abs(containerRect.left - inputRect.left) < 50 &&
                           Math.abs(containerRect.top - inputRect.bottom) < 50;
        
        if (isNearInput) {
          return container;
        }
      }
    }
    
    // Zweiter Fallback: Verwende den Container, der kürzlich erstellt wurde
    // (normalerweise der letzte in der NodeList)
    for (let i = allPacContainers.length - 1; i >= 0; i--) {
      const container = allPacContainers[i];
      const style = window.getComputedStyle(container);
      if (style.display !== 'none' && style.visibility !== 'hidden') {
        return container;
      }
    }
    
    // Finaler Fallback: Verwende den ersten gefundenen Container
    return allPacContainers[0] || null;
  };

  // Handler für den Wechsel vom normalen TextField zur Places API
  const handleEditingModeActivation = () => {
    if (hasPrefilledValue && !isEditingMode) {
      setIsEditingMode(true);
      setHasPrefilledValue(false);
      // Kleine Verzögerung um sicherzustellen, dass die Komponente neu gerendert wird
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }, 100);
    }
  };

  // Input-Focus-Handler für bessere Dropdown-Positionierung
  const handleInputFocus = () => {
    // Platz für die Vorschlagsliste schaffen (siehe ensureRoomBelow).
    if (inputRef.current) ensureRoomBelow(inputRef.current);

    // Wenn wir eine vorausgefüllte Adresse haben, aktiviere den Bearbeitungsmodus
    if (hasPrefilledValue) {
      handleEditingModeActivation();
      return;
    }
    
    // Kleine Verzögerung, damit das Dropdown Zeit hat zu erscheinen
    setTimeout(() => {
      // Finde den spezifischen pac-container für dieses Eingabefeld
      const pacContainer = findAssociatedPacContainer();
      if (pacContainer && inputRef.current) {
        // Finde das übergeordnete TextField-Element für korrekte Positionierung
        const textFieldElement = inputRef.current.closest('.MuiTextField-root');
        const targetElement = textFieldElement || inputRef.current;
        const targetRect = targetElement.getBoundingClientRect();
        
        pacContainer.style.width = `${targetRect.width}px`;
        pacContainer.style.left = `${targetRect.left}px`;
        pacContainer.style.top = `${targetRect.bottom}px`;
        pacContainer.style.position = 'fixed';
        pacContainer.style.zIndex = '1500';
      }
    }, 100);
  };

  // Markierungen für die Tastaturführung. In den reinen TextField-Pfaden gibt
  // es keine Vorschlagsliste, im Legacy-Pfad schon.
  const navProps = navName ? { 'data-nav': navName, 'data-nav-type': 'text' } : {};
  const legacyNavProps = navName ? { 'data-nav': navName, 'data-nav-type': 'place' } : {};

  // Wenn wir eine vorausgefüllte Adresse haben und noch nicht im Bearbeitungsmodus sind,
  // zeige normales TextField
  if (hasPrefilledValue && !isEditingMode) {
    return (
      <TextField
        inputRef={inputRef}
        autoComplete={NO_AUTOFILL}
        {...navProps}
        label={label}
        value={value}
        onChange={onChange}
        onFocus={handleInputFocus}
        error={error}
        helperText={helperText}
        required={required}
        disabled={disabled}
        fullWidth={fullWidth}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <LocationOnIcon />
            </InputAdornment>
          ),
          style: { cursor: 'pointer' }
        }}
        {...textFieldProps}
      />
    );
  }

  // Wenn neue API verfügbar ist, verwende PlaceAutocompleteElementWrapper
  if (useNewApi && !apiError) {
    return (
      <PlaceAutocompleteElementWrapper
        onPlaceSelect={onPlaceSelect}
        label={label}
        value={value}
        onChange={onChange}
        error={error}
        helperText={helperText}
        required={required}
        disabled={disabled}
        fullWidth={fullWidth}
        // Lokaler Bias aus dem Betriebsgebiet
        locationBias={serviceAreaBias?.locationBias}
        navName={navName}
        // State von übergeordneter Komponente weitergeben
        hasPrefilledValue={hasPrefilledValue}
        isEditingMode={isEditingMode}
        onEditingModeActivation={handleEditingModeActivation}
        {...textFieldProps}
      />
    );
  }

  // Fallback für Fehlerfall oder wenn keine API verfügbar
  if (apiError) {
    return (
      <Box>
        <Alert severity="warning" sx={{ mb: 1, fontSize: '0.875rem' }}>
          {apiError} - Verwende einfaches Textfeld
        </Alert>
        <TextField
          autoComplete={NO_AUTOFILL}
          {...navProps}
          label={label}
          value={value}
          onChange={onChange}
          error={error}
          helperText={helperText}
          required={required}
          disabled={disabled}
          fullWidth={fullWidth}
          placeholder="Adresse eingeben..."
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <LocationOnIcon />
              </InputAdornment>
            ),
          }}
          {...textFieldProps}
        />
      </Box>
    );
  }

  return (
    <TextField
      inputRef={inputRef}
      // Beim Legacy-Pfad sitzen data-nav und data-nav-open bewusst auf dem
      // <input> selbst – der Effect oben pflegt data-nav-open dort, und die
      // Tastaturführung erwartet beide Attribute am selben Knoten.
      autoComplete={NO_AUTOFILL}
      inputProps={legacyNavProps}
      label={label}
      value={value}
      onChange={onChange}
      onFocus={handleInputFocus}
      error={error}
      helperText={helperText}
      required={required}
      disabled={disabled}
      fullWidth={fullWidth}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <LocationOnIcon />
          </InputAdornment>
        ),
      }}
      {...textFieldProps}
    />
  );
};

export default PlaceAutocomplete;